import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FiUsers, FiCalendar, FiArrowRight, FiBriefcase } from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  pssPortalService, STATUS_PSS_LABEL,
  type EditalPublico, type StatusPSS,
} from '../../services/pss.service';

export function PssListPublicoPage() {
  const [editais, setEditais] = useState<EditalPublico[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState<'todos' | StatusPSS>('todos');

  useEffect(() => {
    pssPortalService.listarEditais(getEstabelecimentoAtualId())
      .then(setEditais)
      .finally(() => setCarregando(false));
  }, []);

  const filtrados = filtro === 'todos' ? editais : editais.filter((e) => e.status_pss === filtro);

  return (
    <PortalPage
      titulo="Processos seletivos"
      subtitulo="Editais de contratação temporária — Lei 8.745/93"
      icone={<FiBriefcase size={26} />}
      largura="larga"
      heroExtra={
        <div className="flex items-center gap-2 flex-wrap">
          {(['todos', 'aberto', 'encerrado_inscricoes', 'em_analise', 'resultado_final', 'homologado'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition ${
                filtro === f
                  ? 'border-white bg-white text-institutional'
                  : 'border-white/30 bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {f === 'todos' ? 'Todos' : STATUS_PSS_LABEL[f]}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-5">
        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : filtrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiBriefcase className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">
                Nenhum edital {filtro === 'todos' ? 'disponível no momento' : `com status "${STATUS_PSS_LABEL[filtro]}"`}.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtrados.map((e) => (
              <ItemEdital key={e.id} edital={e} />
            ))}
          </div>
        )}

        <Alert variante="info">
          Para se inscrever em um edital, clique nele e siga o fluxo. Você precisa ter cadastro completo
          no portal (CPF, e-mail, telefone, endereço).
        </Alert>
      </div>
    </PortalPage>
  );
}

function ItemEdital({ edital }: { edital: EditalPublico }) {
  const aberto = edital.status_pss === 'aberto';
  return (
    <Link
      to={`/pss/${edital.numero}`}
      className="block bg-white border border-slate-200 rounded-2xl hover:border-institutional/40 hover:shadow-sm transition"
    >
      <div className="p-4 flex items-start gap-4">
        <div className={`flex-shrink-0 w-10 h-10 rounded flex items-center justify-center ${
          aberto ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
        }`}>
          <FiUsers size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500 tabular-nums">{edital.numero}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              aberto ? 'bg-emerald-100 text-emerald-700' :
              edital.status_pss === 'cancelado' ? 'bg-red-100 text-red-700' :
              edital.status_pss === 'homologado' ? 'bg-blue-100 text-blue-700' :
              'bg-slate-100 text-slate-700'
            }`}>
              {STATUS_PSS_LABEL[edital.status_pss]}
            </span>
            {edital.vagas != null && (
              <span className="text-xs text-slate-500">{edital.vagas} vaga{edital.vagas === 1 ? '' : 's'}</span>
            )}
          </div>
          <h2 className="text-base font-medium text-slate-900 mt-1">{edital.titulo}</h2>
          {edital.cargo && <p className="text-sm text-slate-600 mt-0.5">{edital.cargo}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {edital.inscricoes_fim && (
              <span className="inline-flex items-center gap-1">
                <FiCalendar size={11} /> Inscrições até {format(parseISO(edital.inscricoes_fim), "dd/MM/yyyy", { locale: ptBR })}
              </span>
            )}
            <span>{edital.total_inscritos} inscrito{edital.total_inscritos === 1 ? '' : 's'}</span>
          </div>
        </div>
        <FiArrowRight className="text-slate-300 flex-shrink-0 mt-1" size={16} />
      </div>
    </Link>
  );
}
